import { apiClient } from "./base.service";
import { getAuth } from "firebase/auth";
import type { UpdateStartupData } from "../../types/startup.types";

export interface Expense {
  id: string;
  name: string;
  category: string;
  monthlyAmount?: number; // Made optional for percentage-only expenses
  oneTimeAmount?: number; // For one-time expenses
  startDate: string;
  endDate?: string | null;
  dayOfMonth?: number;
  color: string;
  // New scaling fields
  expenseType?: "fixed" | "percentage" | "hybrid" | "customer_based";
  growthRate?: number;
  revenueRatio?: number;
  // Customer-based expense fields
  customerBaseCostPerCustomer?: number;
  customerBaseAmount?: number;
  isOneTime?: boolean; // Flag for one-time expenses
  lineItemGroupId?: string;
  segmentKind?: "initial" | "revision";
  revisesId?: string;
  revisionNote?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Revenue {
  id: string;
  name: string;
  category: "sales" | "grant" | "other";
  monthlyAmount: number;
  startDate: string;
  endDate?: string | null;
  isRecurring: boolean;
  dayOfMonth?: number;
  frequency: "monthly" | "quarterly" | "annual";
  color: string;
  // New scaling fields
  growthRate?: number;
  isCustomerDriven?: boolean;
  lineItemGroupId?: string;
  segmentKind?: "initial" | "revision";
  revisesId?: string;
  revisionNote?: string;
  createdAt: string;
  updatedAt: string;
}

export interface FinancialEvent {
  id: string;
  date: string;
  type: "funding" | "launch" | "milestone" | "other";
  priority: "high" | "medium" | "low";
  description: string;
  amount?: number | null;
  icon: string;
  color: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateExpenseData {
  name: string;
  category: string;
  monthlyAmount?: number; // Made optional for percentage-only expenses
  oneTimeAmount?: number; // For one-time expenses
  startDate: string;
  endDate?: string;
  dayOfMonth?: number;
  color: string;
  // New scaling fields
  expenseType?: "fixed" | "percentage" | "hybrid" | "customer_based";
  growthRate?: number;
  revenueRatio?: number;
  // Customer-based expense fields
  customerBaseCostPerCustomer?: number;
  customerBaseAmount?: number;
  isOneTime?: boolean; // Flag for one-time expenses
}

export interface CreateRevenueData {
  name: string;
  category: "sales" | "grant" | "other";
  monthlyAmount: number;
  startDate: string;
  endDate?: string;
  isRecurring: boolean;
  dayOfMonth?: number;
  frequency: "monthly" | "quarterly" | "annual";
  color: string;
  // New scaling fields
  growthRate?: number;
}

export interface CreateEventData {
  date: string;
  type: "funding" | "launch" | "milestone" | "asset" | "other";
  priority: "high" | "medium" | "low";
  description: string;
  amount?: number;
  icon: string;
  color: string;
  // Asset-specific fields
  monthlyAppreciationRate?: number;
  minimumValue?: number;
  endDate?: string;
  lineItemGroupId?: string;
  segmentKind?: "initial" | "revision";
  revisesId?: string;
  revisionNote?: string;
  suppressPurchaseCashFlow?: boolean;
  capacityGroupId?: string;
  projectionId?: string;
  capacityIncrease?: number;
}

export interface ReviseAssetEventData {
  effectiveDate: string;
  amount: number;
  monthlyAppreciationRate: number;
  minimumValue?: number;
  revisionNote?: string;
}

export interface ReviseRevenueData {
  effectiveDate: string;
  monthlyAmount: number;
  growthRate?: number;
  frequency?: "monthly" | "quarterly" | "annual";
  revisionNote?: string;
}

export interface ReviseExpenseData {
  effectiveDate: string;
  monthlyAmount?: number;
  growthRate?: number;
  revenueRatio?: number;
  revisionNote?: string;
}

export class FinancialAPIService {
  private async getAuthHeaders() {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error("User not authenticated");
    }

    const token = await user.getIdToken();
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };
  }

  // Expenses
  async getExpenses(startupId: string): Promise<Expense[]> {
    const headers = await this.getAuthHeaders();
    return apiClient.get(`/startups/${startupId}/expenses`, { headers });
  }

  async createExpense(
    startupId: string,
    data: CreateExpenseData,
  ): Promise<Expense> {
    const headers = await this.getAuthHeaders();
    return apiClient.post(`/startups/${startupId}/expenses`, data, { headers });
  }

  async updateExpense(
    startupId: string,
    expenseId: string,
    data: Partial<CreateExpenseData>,
  ): Promise<Expense> {
    console.log('FinancialAPIService: updateExpense called with data:', data);
    const headers = await this.getAuthHeaders();
    const result = await apiClient.put(`/startups/${startupId}/expenses/${expenseId}`, data, {
      headers,
    }) as Expense;
    console.log('FinancialAPIService: updateExpense result:', result);
    return result;
  }

  async deleteExpense(
    startupId: string,
    expenseId: string,
  ): Promise<{ success: boolean }> {
    const headers = await this.getAuthHeaders();
    return apiClient.delete(`/startups/${startupId}/expenses/${expenseId}`, {
      headers,
    });
  }

  async reviseExpense(
    startupId: string,
    expenseId: string,
    data: ReviseExpenseData,
  ): Promise<{ previousSegment: Expense; newSegment: Expense }> {
    const headers = await this.getAuthHeaders();
    return apiClient.post(
      `/startups/${startupId}/expenses/${expenseId}/revise`,
      data,
      { headers },
    );
  }

  // Revenues
  async getRevenues(startupId: string): Promise<Revenue[]> {
    const headers = await this.getAuthHeaders();
    return apiClient.get(`/startups/${startupId}/revenues`, { headers });
  }

  async createRevenue(
    startupId: string,
    data: CreateRevenueData,
  ): Promise<Revenue> {
    const headers = await this.getAuthHeaders();
    return apiClient.post(`/startups/${startupId}/revenues`, data, { headers });
  }

  async updateRevenue(
    startupId: string,
    revenueId: string,
    data: Partial<CreateRevenueData>,
  ): Promise<Revenue> {
    const headers = await this.getAuthHeaders();
    return apiClient.put(`/startups/${startupId}/revenues/${revenueId}`, data, {
      headers,
    });
  }

  async deleteRevenue(
    startupId: string,
    revenueId: string,
  ): Promise<{ success: boolean }> {
    const headers = await this.getAuthHeaders();
    return apiClient.delete(`/startups/${startupId}/revenues/${revenueId}`, {
      headers,
    });
  }

  async reviseRevenue(
    startupId: string,
    revenueId: string,
    data: ReviseRevenueData,
  ): Promise<{ previousSegment: Revenue; newSegment: Revenue }> {
    const headers = await this.getAuthHeaders();
    return apiClient.post(
      `/startups/${startupId}/revenues/${revenueId}/revise`,
      data,
      { headers },
    );
  }

  // Events
  async getEvents(startupId: string): Promise<FinancialEvent[]> {
    const headers = await this.getAuthHeaders();
    return apiClient.get(`/startups/${startupId}/events`, { headers });
  }

  async createEvent(
    startupId: string,
    data: CreateEventData,
  ): Promise<FinancialEvent> {
    const headers = await this.getAuthHeaders();
    return apiClient.post(`/startups/${startupId}/events`, data, { headers });
  }

  async updateEvent(
    startupId: string,
    eventId: string,
    data: Partial<CreateEventData>,
  ): Promise<FinancialEvent> {
    const headers = await this.getAuthHeaders();
    return apiClient.put(`/startups/${startupId}/events/${eventId}`, data, {
      headers,
    });
  }

  async deleteEvent(
    startupId: string,
    eventId: string,
  ): Promise<{ success: boolean }> {
    const headers = await this.getAuthHeaders();
    return apiClient.delete(`/startups/${startupId}/events/${eventId}`, {
      headers,
    });
  }

  async reviseAssetEvent(
    startupId: string,
    eventId: string,
    data: ReviseAssetEventData,
  ): Promise<{ previousSegment: FinancialEvent; newSegment: FinancialEvent }> {
    const headers = await this.getAuthHeaders();
    return apiClient.post(
      `/startups/${startupId}/events/${eventId}/revise`,
      data,
      { headers },
    );
  }

  async undoExpenseRevision(
    startupId: string,
    expenseId: string,
  ): Promise<{ success: boolean; restoredSegment: Expense }> {
    const headers = await this.getAuthHeaders();
    return apiClient.delete(
      `/startups/${startupId}/expenses/${expenseId}/revision`,
      { headers },
    );
  }

  async undoRevenueRevision(
    startupId: string,
    revenueId: string,
  ): Promise<{ success: boolean; restoredSegment: Revenue }> {
    const headers = await this.getAuthHeaders();
    return apiClient.delete(
      `/startups/${startupId}/revenues/${revenueId}/revision`,
      { headers },
    );
  }

  async undoAssetRevision(
    startupId: string,
    eventId: string,
  ): Promise<{ success: boolean; restoredSegment: FinancialEvent }> {
    const headers = await this.getAuthHeaders();
    return apiClient.delete(
      `/startups/${startupId}/events/${eventId}/revision`,
      { headers },
    );
  }

  // Complete startup financial data
  async getCompleteStartupData(startupId: string) {
    const headers = await this.getAuthHeaders();
    return apiClient.get(`/startups/${startupId}/complete`, { headers });
  }

  // Update startup metadata
  // Update startup metadata
  async updateStartupMetadata(
    startupId: string,
    data: {
      startDate?: string;
      endDate?: string;
      initialBalance?: number;
    },
  ) {
    const headers = await this.getAuthHeaders();
    return apiClient.put(`/startups/${startupId}`, data, { headers });
  }

  // Update startup settings
  async updateStartup(
    startupId: string,
    data: UpdateStartupData,
  ) {
    const headers = await this.getAuthHeaders();
    return apiClient.put(`/startups/${startupId}`, data, { headers });
  }
}

export const financialAPI = new FinancialAPIService();
